import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import { MouseEventHandler, forwardRef, useState } from 'react';

import { ActionableLink, HUIAlert, HUIImage, HUIBadges } from 'root/widgets/common-components';

import { AgentInfo } from 'root/widgets/agent-info';

import { extractDataAutomationId, filterMetadata } from 'root/widgets/utils';

import './contact-agent-card.scss';
import { ContactAgentCardProps } from './types';

const ContactAgentCard = forwardRef<HTMLDivElement, ContactAgentCardProps>(
  (
    { agentInfoProps: agentInfo, contactActions, liveTour, agency, userIntent, termsAndPolicy, metadata, context },
    ref,
  ) => {
    const [isIntentSelected, setIntentSelected] = useState<boolean>(!isEmpty(userIntent?.selected));
    const [shouldShowIntentAlert, setShowIntentAlert] = useState<boolean>(false);

    const handleIntentClick: MouseEventHandler = (event) => {
      setIntentSelected(true);
      setShowIntentAlert(false);
      userIntent?.onClick?.(event);
    };

    const handleDisableCTAClick: MouseEventHandler = (event) => {
      if (userIntent?.alertText && !isIntentSelected) {
        event.preventDefault();
        setShowIntentAlert(true);
      }
    };

    return (
      <div
        ref={ref}
        className="contact-agent-card-root"
        {...filterMetadata(metadata)}
        {...extractDataAutomationId(metadata)}
      >
        <div className="card-header">
          {agency && (
            <div className="agency">
              {agency.avatar && <HUIImage className="agency-avatar" src={agency.avatar} alt={agency.name} />}
              {agency.name}
            </div>
          )}
          <AgentInfo {...agentInfo} context={context} />
        </div>
        <div className="card-body">
          {shouldShowIntentAlert && userIntent?.alertText && (
            <HUIAlert variant="warning" className={classNames('intent-alert')}>
              {userIntent?.alertText}
            </HUIAlert>
          )}
          {userIntent && <HUIBadges {...userIntent} onClick={handleIntentClick} />}
          <div
            className={classNames('contact-agent-actions', { disabled: userIntent && !isIntentSelected })}
            onClick={handleDisableCTAClick}
          >
            {contactActions.map((action, index) => (
              <ActionableLink key={`contact-agent-${index}`} className="btn-outline-secondary btn" {...action} />
            ))}
          </div>
          {termsAndPolicy && <div className="terms-and-policy">{termsAndPolicy}</div>}
        </div>
        {liveTour && (
          <div className="card-footer">
            <div className="live-tour">
              <div className="live-tour-badge">
                <i className="pgicon-video-call" />
                {liveTour.text}
              </div>
              {liveTour.description}
            </div>
          </div>
        )}
      </div>
    );
  },
);

ContactAgentCard.displayName = 'ContactAgentCard';

export default ContactAgentCard;
